import { FC } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { adminCreateUser } from '@shared/api/user/user.controller';
import { UserCreate } from '@shared/api/user/user.interface';
import { Button } from '@shared/components/Button';
import { LoadingModal } from '@shared/components/LoadingModal';
import Modal from '@shared/components/Modal';
import { TextField } from '@shared/components/TextField';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { UserCustomTheme, UserRole } from '@shared/types';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useAsyncFn } from 'react-use';

export const ADMIN_USER_CREATE_MODAL_OPEN_ID = 'admin-user-manage-create';

interface CreateFormInputs {
  username: string;
  password: string;
  name: string;
  phoneNumber: string;
  role: UserRole;
}

interface Props {
  //* 생성 가능한 권한 목록
  roleOptions: UserRole[];
  //* 생성 완료 후 콜백 (목록 새로고침 등)
  onCreated: () => void;
}

//* 관리자가 신규 계정을 생성할 때 사용하는 모달
export const AdminUserCreateModal: FC<Props> = function AdminUserCreateModal({
  roleOptions,
  onCreated,
}: Props) {
  const notiInstance = useRecoilValue(notificationInstanceAtom);
  const setOpen = useSetRecoilState(
    simpleOpenAtomFamily(ADMIN_USER_CREATE_MODAL_OPEN_ID),
  );

  const { register, handleSubmit, reset } = useForm<CreateFormInputs>({
    defaultValues: {
      username: '',
      password: '',
      name: '',
      phoneNumber: '',
      role: roleOptions[0],
    },
  });

  const [{ loading }, submit] = useAsyncFn(
    async (data: CreateFormInputs) => {
      try {
        await adminCreateUser({
          username: data.username,
          password: data.password,
          roles: [data.role],
          info: {
            name: data.name,
            gender: '',
            birth: '',
            phoneNumber: data.phoneNumber,
            theme: UserCustomTheme.Default,
          },
        } as UserCreate);
        notificateSuccess(notiInstance, '계정 생성 완료');
        reset();
        setOpen(false);
        onCreated();
      } catch (err) {
        notificateError(notiInstance, '계정 생성 실패');
        console.error(err);
      }
    },
    [notiInstance, onCreated, setOpen, reset],
  );

  const onSubmit: SubmitHandler<CreateFormInputs> = (data) => {
    submit(data);
  };

  return (
    <Modal openId={ADMIN_USER_CREATE_MODAL_OPEN_ID}>
      <form className="space-y-4 pt-2" onSubmit={handleSubmit(onSubmit)}>
        <h2 className="text-16 font-bold">계정 생성</h2>
        <div className="grid grid-cols-1 gap-3">
          <TextField
            {...register('username', { required: true })}
            label="아이디"
            placeholder="아이디"
            autoComplete="off"
            className="w-full"
          />
          <TextField
            {...register('password', { required: true })}
            label="비밀번호"
            placeholder="비밀번호"
            type="password"
            autoComplete="new-password"
            className="w-full"
          />
          <div className="space-y-1">
            <label className="text-14 font-semibold">권한</label>
            <select
              {...register('role', { required: true })}
              className="w-full rounded border px-2 py-2 text-14"
            >
              {roleOptions.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <TextField
            {...register('name')}
            label="이름"
            placeholder="이름"
            className="w-full"
          />
          <TextField
            {...register('phoneNumber')}
            label="전화번호"
            placeholder="전화번호"
            className="w-full"
          />
        </div>
        <div className="pt-2">
          <Button
            disabled={loading}
            type="submit"
            className="button-rectangle min-h-[40px] w-full rounded-md theme-text-3 theme-bg-main"
          >
            생성
          </Button>
        </div>
      </form>
      <LoadingModal open={loading} title="생성중" />
    </Modal>
  );
};
